import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { CircularProgress } from "@mui/material";

const customModalStyles = {
    content: {
        padding: "20px",
        width: "80%",
        maxWidth: "600px",
        margin: "auto",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
};

const isImageFile = (url) =>
    typeof url === "string" && /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(url);

const parseBooleanDownloadable = (val) => {
    if (val === false || val === 0 || val === "0" || val === "false") {
        return false;
    }
    return true;
};

const getResourcePreview = (fileUrl) => {
    if (isImageFile(fileUrl)) {
        return { type: "image", src: fileUrl };
    }
    if (typeof fileUrl === "string" && fileUrl) {
        return { type: "file", src: fileUrl };
    }
    return { type: "placeholder", src: null };
};

const resetFormState = () => ({
    file: "",
    name: "",
    deadline: "",
    type: "",
    category: "",
    display: "yes",
    isDownloadable: true,
    programId: "",
    programName: "",
});

export default function Resource() {
    const [resources, setresources] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editResourceId, seteditResourceId] = useState(null);
    const [sortByLatest, setSortByLatest] = useState(false);
    const [formData, setformData] = useState({
        file: "",
        name: "",
        deadline: "",
        type: "",
        category: "",
        display: "yes",
        isDownloadable: true,
        programId: "",
        programName: "",
    });
    const [resourceFetchLoading, setResourceFetchLoading] = useState(false);
    const [buttonLoading, setbuttonLoading] = useState(false);
    const [imgFile, setImgFile] = useState(null);
    const [filter, setFilter] = useState("all");
    const [imgPreview, setImgPreview] = useState(null);
    useEffect(() => {
        // Set image preview when formData.img changes (for edit case)
        if (formData.file) {
            setImgPreview(formData.file);
        }
    }, [formData.file]);

    useEffect(() => {
        // Fetch resources and programs on component mount
        fetchResources();
        fetchPrograms();
    }, []);
    const filteredResources = resources.filter((resource) =>
        filter === "all" ? true : resource.type === filter
    );
    // Fetch all resources
    const fetchResources = async () => {
        setResourceFetchLoading(true);
        try {
            const response = await axiosInstance.get("resource/all");
            setresources(response.data.data);
        } catch (error) {
            console.error("Error fetching resources:", error);
        }
        setResourceFetchLoading(false);
    };

    // Fetch all programs (from /programs)
    const fetchPrograms = async () => {
        try {
            const response = await axiosInstance.get("program/all");
            setPrograms(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching programs:", error);
        }
    };

    const handleProgramChange = (e) => {
        const selectedId = e.target.value;
        const selectedProg = programs.find(
            (p) => String(p._id || p.id) === String(selectedId)
        );
        setformData((prevData) => ({
            ...prevData,
            programId: selectedId,
            programName: selectedProg ? selectedProg.title : "",
        }));
    };

    // Handle create Resource
    const handleCreateResource = async (e) => {
        e.preventDefault();
        setbuttonLoading(true);
        const formDataToSend = new FormData();
        Object.keys(formData).forEach((key) => {
            if (key === "file") {
                return;
            }
            if (key === "isDownloadable") {
                formDataToSend.append("isDownloadable", formData.isDownloadable ? "1" : "0");
                return;
            }
            formDataToSend.append(key, formData[key] || "");
        });
        if (imgFile) {
            formDataToSend.append("file", imgFile);
        }
        try {
            await axiosInstance.post("resource/create", formDataToSend);
            fetchResources();
            setShowCreateModal(false);
            Swal.fire("Success!", "Resource created successfully!", "success", 1500);
            setformData(resetFormState());
            setImgFile(null);
            setImgPreview(null);
        } catch (error) {
            console.error("Error creating Resource:", error);
            const message = error.response?.data?.message || "Failed to create Resource";
            Swal.fire("Error", message, "error");
        }
        setbuttonLoading(false);
    };

    // Handle edit Resource
    const handleEditResource = async (e) => {
        e.preventDefault();
        setbuttonLoading(true);
        const formDataToSend = new FormData();
        Object.keys(formData).forEach((key) => {
            if (key === "file") {
                return;
            }
            if (key === "isDownloadable") {
                formDataToSend.append("isDownloadable", formData.isDownloadable ? "1" : "0");
                return;
            }
            formDataToSend.append(key, formData[key] || "");
        });

        if (imgFile) {
            formDataToSend.append("file", imgFile);
        }
        formDataToSend.append("_method", "PUT");
        try {
            await axiosInstance.post(`resource/change/${editResourceId}`, formDataToSend);
            fetchResources();
            setShowEditModal(false);
            Swal.fire("Success!", "Resource updated successfully!", "success");
        } catch (error) {
            console.error("Error updating Resource:", error);
            const message = error.response?.data?.message || "Failed to update Resource";
            Swal.fire("Error", message, "error");
        }
        setbuttonLoading(false);
    };
    const handleViewResource = async (id) => {
        setShowEditModal(true);

        try {
            seteditResourceId(id);
            const response = await axiosInstance.get(`resource/single/${id}`);
            const data = response?.data?.data;

            setformData({
                name: data?.name || "",
                deadline: data?.deadline || "",
                type: data?.type || "",
                category: data?.category || "",
                display: data?.display || "",
                isDownloadable: parseBooleanDownloadable(data?.isDownloadable),
                file: data?.file || "",
                programId: data?.programId || data?.program?._id || data?.program?.id || "",
                programName: data?.programName || data?.program?.title || "",
            });
        } catch (error) {
            console.error("Error updating Resource:", error);
            Swal.fire("Error", "Failed to update Resource", "error");
        }
    };
    // Handle delete Resource
    const handleDeleteResource = async (id) => {
        try {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete it!",
            });
            if (result.isConfirmed) {
                await axiosInstance.delete(`resource/delete/${id}`);
                fetchResources();
                Swal.fire("Deleted!", "Resource deleted successfully", "success", 1500);
            }
        } catch (error) {
            console.error("Error deleting Resource:", error);
            Swal.fire("Error", "Failed to delete Resource", "error");
        }
    };
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setformData((prevData) => ({
            ...prevData,
            [name]: type === "checkbox" ? checked : value,
        }));
    };
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImgFile(file);
            setImgPreview(URL.createObjectURL(file));
        }
    };
    const sortedResources = sortByLatest
        ? [...filteredResources].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : filteredResources || [];
    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                    onClick={() => setShowCreateModal(true)}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: "#4CAF50",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        marginBottom: "20px",
                        cursor: "pointer",
                    }}
                >
                    Create New Resource
                </button>
                <button
                    onClick={() => setSortByLatest((prev) => !prev)}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: sortByLatest ? "#2196F3" : "#f0f0f0",
                        color: sortByLatest ? "#fff" : "#000",
                        border: "none",
                        borderRadius: "5px",
                        marginBottom: "20px",
                        cursor: "pointer",
                        marginRight: "150px",
                    }}
                >
                    {sortByLatest ? "Show Default" : "Show Latest"}
                </button>
            </div>
            <div style={{ marginBottom: "20px" }}>
                <button
                    onClick={() => setFilter("all")}
                    style={{
                        padding: "10px 20px",
                        marginRight: "10px",
                        backgroundColor: filter === "all" ? "#4CAF50" : "#ccc",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    All
                </button>
                <button
                    onClick={() => setFilter("advertisement")}
                    style={{
                        padding: "10px 20px",
                        marginRight: "10px",
                        backgroundColor: filter === "advertisement" ? "#4CAF50" : "#ccc",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    Ads
                </button>
                <button
                    onClick={() => setFilter("merit")}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: filter === "merit" ? "#4CAF50" : "#ccc",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    Merit
                </button>
                <button
                    onClick={() => setFilter("downloads")}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: filter === "downloads" ? "#4CAF50" : "#ccc",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginLeft: "15px",
                    }}
                >
                    downloads
                </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
                {resourceFetchLoading ? (
                    <div
                        style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 9999,
                        }}
                    >
                        {" "}
                        <CircularProgress />{" "}
                    </div>
                ) : (
                    sortedResources.map((Resource) => {
                        const preview = getResourcePreview(Resource.file);
                        return (
                            <div
                                key={Resource._id}
                                style={{
                                    width: "250px",
                                    padding: "15px",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    textAlign: "center",
                                }}
                            >
                                {preview.type === "image" ? (
                                    <img
                                        src={preview.src}
                                        alt={Resource.name}
                                        style={{
                                            width: "100%",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            marginBottom: "15px",
                                            aspectRatio: "16/9",
                                        }}
                                    />
                                ) : (
                                    <a
                                        href={preview.src || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "100%",
                                            aspectRatio: "16/9",
                                            borderRadius: "8px",
                                            marginBottom: "15px",
                                            backgroundColor: "#f3f4f6",
                                            color: "#374151",
                                            textDecoration: "none",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {preview.type === "file" ? "View PDF / File" : "No preview"}
                                    </a>
                                )}
                                <h3>{Resource.name}</h3>
                                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px", marginBottom: "8px" }}>
                                    {Resource.programName && (
                                        <div
                                            style={{
                                                display: "inline-block",
                                                padding: "3px 8px",
                                                backgroundColor: "#e0f2fe",
                                                color: "#0369a1",
                                                borderRadius: "12px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            {Resource.programName}
                                        </div>
                                    )}
                                    {parseBooleanDownloadable(Resource.isDownloadable) ? (
                                        <div
                                            style={{
                                                display: "inline-block",
                                                padding: "3px 8px",
                                                backgroundColor: "#ecfdf5",
                                                color: "#047857",
                                                borderRadius: "12px",
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                border: "1px solid #a7f3d0",
                                            }}
                                        >
                                            ✓ Downloadable
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                display: "inline-block",
                                                padding: "3px 8px",
                                                backgroundColor: "#fffbeb",
                                                color: "#b45309",
                                                borderRadius: "12px",
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                border: "1px solid #fde68a",
                                            }}
                                        >
                                            🔒 View Only
                                        </div>
                                    )}
                                </div>
                                <p>{Resource.type}</p>
                                <Actions
                                    courseId={Resource._id}
                                    onEdit={handleViewResource}
                                    onDelete={handleDeleteResource}
                                />
                            </div>
                        );
                    })
                )}
            </div>

            <Modal
                isOpen={showCreateModal}
                onRequestClose={() => {
                    setformData(resetFormState());
                    setImgFile(null);
                    setImgPreview(null);
                    setShowCreateModal(false);
                }}
                style={customModalStyles}
                contentLabel="Create New Resource"
            >
                <h2>Create New Resource</h2>
                <form onSubmit={handleCreateResource}>
                    <div style={{ marginBottom: "10px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: "bold",
                                marginBottom: "5px",
                            }}
                        >
                            Specific Program (from /programs)
                        </label>
                        <select
                            name="programId"
                            value={formData.programId || ""}
                            onChange={handleProgramChange}
                            style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        >
                            <option value="">-- None / General (or select program) --</option>
                            {programs.map((prog) => (
                                <option key={prog._id || prog.id} value={prog._id || prog.id}>
                                    {prog.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {[
                        { label: "name", name: "name", type: "text", required: true },

                        { label: "Type", name: "type", type: "dropdown", options: ["advertisement", "merit", "downloads"] },

                        { label: "Display", name: "display", type: "text" },
                        { label: "Deadline", name: "deadline", type: "date" },

                    ].map(({ label, name, type, required, options }) => (
                        <div key={name} style={{ marginBottom: "10px" }}>
                            <label
                                style={{
                                    display: "block",
                                    fontWeight: "bold",
                                    marginBottom: "5px",
                                }}
                            >
                                {label}
                            </label>
                            {type === "dropdown" ? (
                                <select
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                    }}
                                >
                                    <option value="">Select an option</option>
                                    {options.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            ) : type === "textarea" ? (
                                <textarea
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        height: "100px",
                                    }}
                                />
                            ) : (
                                <input
                                    type={type}
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {/* Downloadable Checkbox */}
                    <div
                        style={{
                            marginBottom: "15px",
                            padding: "12px",
                            backgroundColor: "#f8fafc",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "14px",
                                color: "#1e293b",
                            }}
                        >
                            <input
                                type="checkbox"
                                name="isDownloadable"
                                checked={formData.isDownloadable !== false}
                                onChange={(e) =>
                                    setformData((prev) => ({
                                        ...prev,
                                        isDownloadable: e.target.checked,
                                    }))
                                }
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    marginRight: "10px",
                                    cursor: "pointer",
                                }}
                            />
                            <span>Downloadable (Allow File Download)</span>
                        </label>
                        <p
                            style={{
                                margin: "4px 0 0 28px",
                                fontSize: "12px",
                                color: "#64748b",
                                lineHeight: "1.4",
                            }}
                        >
                            Checked by default. If unchecked, visitors on the public website can only view this document in browser and cannot download or copy it.
                        </p>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: "bold",
                                marginBottom: "5px",
                            }}
                        >
                            Upload
                        </label>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            required
                            style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>
                    {imgPreview && (
                        <div style={{ marginBottom: "15px" }}>
                            {isImageFile(imgPreview) ? (
                                <img
                                    src={imgPreview}
                                    alt="Preview"
                                    style={{ width: "30%", height: "auto", borderRadius: "4px" }}
                                />
                            ) : (
                                <a
                                    href={imgPreview}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-block",
                                        padding: "8px 12px",
                                        backgroundColor: "#f1f5f9",
                                        color: "#2563eb",
                                        borderRadius: "6px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        textDecoration: "none",
                                        border: "1px solid #cbd5e1",
                                    }}
                                >
                                    📄 Current File: View Document
                                </a>
                            )}
                        </div>
                    )}
                    <button
                        type="submit"
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#4CAF50",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            marginTop: "10px",
                        }}
                    >
                        {buttonLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Create"
                        )}
                    </button>
                </form>
            </Modal>

            <Modal
                isOpen={showEditModal}
                onRequestClose={() => {
                    setformData(resetFormState());
                    setImgPreview(null);
                    setImgFile(null);
                    seteditResourceId(null);
                    setShowEditModal(false);
                }}
                style={customModalStyles}
                contentLabel="Edit Resource"
            >
                <h2>Edit Resource</h2>
                <form onSubmit={handleEditResource}>
                    <div style={{ marginBottom: "10px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: "bold",
                                marginBottom: "5px",
                            }}
                        >
                            Specific Program (from /programs)
                        </label>
                        <select
                            name="programId"
                            value={formData.programId || ""}
                            onChange={handleProgramChange}
                            style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        >
                            <option value="">-- None / General (or select program) --</option>
                            {programs.map((prog) => (
                                <option key={prog._id || prog.id} value={prog._id || prog.id}>
                                    {prog.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {[
                        { label: "name", name: "name", type: "text" },

                        { label: "Type", name: "type", type: "dropdown", options: ["advertisement", "merit", "downloads"] },

                        { label: "Display", name: "display", type: "text" },
                        { label: "Deadline", name: "deadline", type: "date" },
                    ].map(({ label, name, type, required, options }) => (
                        <div key={name} style={{ marginBottom: "10px" }}>
                            <label
                                style={{
                                    display: "block",
                                    fontWeight: "bold",
                                    marginBottom: "5px",
                                }}
                            >
                                {label}
                            </label>
                            {type === "dropdown" ? (
                                <select
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                    }}
                                >
                                    <option value="">Select an option</option>
                                    {options.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            ) : type === "textarea" ? (
                                <textarea
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        height: "100px", // Adjust height as needed
                                    }}
                                />
                            ) : (
                                <input
                                    type={type}
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleChange}
                                    required={required}
                                    style={{
                                        width: "100%",
                                        padding: "8px",
                                        fontSize: "14px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {/* Downloadable Checkbox */}
                    <div
                        style={{
                            marginBottom: "15px",
                            padding: "12px",
                            backgroundColor: "#f8fafc",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "14px",
                                color: "#1e293b",
                            }}
                        >
                            <input
                                type="checkbox"
                                name="isDownloadable"
                                checked={formData.isDownloadable !== false}
                                onChange={(e) =>
                                    setformData((prev) => ({
                                        ...prev,
                                        isDownloadable: e.target.checked,
                                    }))
                                }
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    marginRight: "10px",
                                    cursor: "pointer",
                                }}
                            />
                            <span>Downloadable (Allow File Download)</span>
                        </label>
                        <p
                            style={{
                                margin: "4px 0 0 28px",
                                fontSize: "12px",
                                color: "#64748b",
                                lineHeight: "1.4",
                            }}
                        >
                            Checked by default. If unchecked, visitors on the public website can only view this document in browser and cannot download or copy it.
                        </p>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: "bold",
                                marginBottom: "5px",
                            }}
                        >
                            Upload
                        </label>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>
                    {imgPreview && (
                        <div style={{ marginBottom: "15px" }}>
                            {isImageFile(imgPreview) ? (
                                <img
                                    src={imgPreview}
                                    alt="Preview"
                                    style={{ width: "30%", height: "auto", borderRadius: "4px" }}
                                />
                            ) : (
                                <a
                                    href={imgPreview}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-block",
                                        padding: "8px 12px",
                                        backgroundColor: "#f1f5f9",
                                        color: "#2563eb",
                                        borderRadius: "6px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        textDecoration: "none",
                                        border: "1px solid #cbd5e1",
                                    }}
                                >
                                    📄 Current File: View Document
                                </a>
                            )}
                        </div>
                    )}
                    <button
                        type="submit"
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#4CAF50",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            marginTop: "10px",
                        }}
                    >
                        {buttonLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Update"
                        )}
                    </button>
                </form>
            </Modal>
        </div>
    );
}